import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Tooltip,
} from '@mui/material';
import { Campaign, CampaignFormData, TargetingCondition, TestProtocol } from '../types'; // Import CampaignFormData, TargetingCondition, TestProtocol
import { useFormState, useSnackbar } from '../hooks';
import { useCreateCampaignMutation } from '../hooks/api';
import TestProtocolManager from './TestProtocolManager'; // Import TestProtocolManager component
import TargetingConditionsManager from './TargetingConditionsManager'; // Added import
import FieldLabel from './common/FieldLabel'; // Import the shared FieldLabel component
import { useAuth } from '../contexts/AuthContext';

const NewCampaign: React.FC = () => {
  const navigate = useNavigate();
  const { canAddCampaigns } = useAuth();
  const createCampaignMutation = useCreateCampaignMutation();

  // Use CampaignFormData for initial state, excluding status
  const { formData, setFormData, handleInputChange } = useFormState<CampaignFormData>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    output_format: 'parquet', // Default to parquet for better performance and compression
    targeting_conditions: [], // Initialize targeting_conditions
    structured_test_protocols: [], // Initialize structured_test_protocols
    retention_days: 365, // Default retention_days to 365 for new campaigns
    // Status is not set here, backend defaults it
  });
  const [error, setError] = useState<string | null>(null);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    // Redirect if user doesn't have permission to create campaigns
    if (!canAddCampaigns()) {
      showSnackbar('You do not have permission to create campaigns', 'error');
      navigate('/campaigns');
    }
  }, [canAddCampaigns, navigate, showSnackbar]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!formData.name) {
      setError('Campaign name is required');
      return;
    }

    try {
      setError(null);
      const newCampaignData: Partial<Campaign> = {
        ...formData,
      };

      const result = await createCampaignMutation.mutateAsync(newCampaignData);

      showSnackbar('Campaign created successfully!', 'success');

      // Redirect to the campaign details page after a short delay
      setTimeout(() => {
        navigate(`/campaigns/${result.campaign_id}`);
      }, 1500);
    } catch (err) {
      setError(`Failed to create campaign: ${(err as Error).message}`);
      showSnackbar(`Failed to create campaign: ${(err as Error).message}`, 'error');
    }
  };

  // Handle structured test protocols changes
  const handleStructuredProtocolsChange = (protocols: TestProtocol[]) => {
    setFormData(prev => ({ ...prev, structured_test_protocols: protocols }));
  };

  // Handle targeting conditions changes
  const handleTargetingConditionsChange = (conditions: TargetingCondition[]) => {
    setFormData(prev => ({ ...prev, targeting_conditions: conditions }));
  };

  // Handle Select change for output_format
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value as 'csv' | 'parquet' }));
  };

  return (
    <div>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Create New Campaign
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/campaigns')}>
          Cancel
        </Button>
      </Box>
      <Paper sx={{ p: 3 }}>
        {createCampaignMutation.isPending && <LinearProgress sx={{ mb: 3 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label={
                  <FieldLabel
                    label="Campaign Name"
                    tooltip="A unique and descriptive name for your data collection campaign."
                  />
                }
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                margin="normal"
                required
                error={!formData.name}
                helperText={!formData.name ? 'Campaign name is required' : ''}
                disabled={createCampaignMutation.isPending}
              />
            </Grid>
            {/* Removed empty grid item */}
            <Grid size={12}>
              <TextField
                fullWidth
                label={
                  <FieldLabel
                    label="Description"
                    tooltip="Detailed information about the purpose and goals of this campaign."
                  />
                }
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={3}
                disabled={createCampaignMutation.isPending}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label={
                  <FieldLabel
                    label="Start Date"
                    tooltip="The date when this campaign should begin collecting data."
                  />
                }
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleInputChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={createCampaignMutation.isPending}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label={
                  <FieldLabel
                    label="End Date"
                    tooltip="The date when this campaign should stop collecting data (optional)."
                  />
                }
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleInputChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={createCampaignMutation.isPending}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormControl fullWidth margin="normal" disabled={createCampaignMutation.isPending}>
                <InputLabel id="output-format-label">
                  <FieldLabel
                    label="Output Format"
                    tooltip="The desired format for the processed data files (e.g., CSV, Parquet)."
                  />
                </InputLabel>
                <Select
                  labelId="output-format-label"
                  name="output_format"
                  value={formData.output_format}
                  onChange={handleSelectChange}
                  label={
                    <FieldLabel
                      label="Output Format"
                      tooltip="The desired format for the processed data files (e.g., CSV, Parquet)."
                    />
                  }
                >
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="parquet">Parquet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                label={
                  <FieldLabel
                    label="Data Retention Time (days)"
                    tooltip="Number of days to retain data in S3. Leave empty for indefinite retention."
                  />
                }
                name="retention_days"
                type="number"
                value={formData.retention_days ?? ''} // Handle undefined case for input
                onChange={handleInputChange}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={createCampaignMutation.isPending}
              />
            </Grid>
            <Grid size={12}>
              {' '}
              <TargetingConditionsManager
                conditions={formData.targeting_conditions ?? []}
                onConditionsChange={handleTargetingConditionsChange}
                disabled={createCampaignMutation.isPending}
              />
            </Grid>
            {/* Test Protocol Manager */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Tooltip title="A Test Protocol is a JSON file that specifies data collection instructions for target devices.">
                <Typography variant="h6" gutterBottom>
                  Test Protocols
                </Typography>
              </Tooltip>
              <TestProtocolManager
                protocols={formData.structured_test_protocols ?? []}
                onProtocolsChange={handleStructuredProtocolsChange}
                availableTargetingConditions={formData.targeting_conditions ?? []} // Pass targeting_conditions
                disabled={createCampaignMutation.isPending}
              />
            </Grid>

            <Grid size={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={createCampaignMutation.isPending || !formData.name}
                >
                  {createCampaignMutation.isPending ? 'Creating...' : 'Create Campaign'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default NewCampaign;
